import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  Tooltip,
  Link
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * ResearchPreviewModal - Shows preview of website analysis results before running full job
 * Matches the styling of PreviewModal.js (List Cleaner) for consistency
 */
const ResearchPreviewModal = ({
  open,
  onClose,
  onConfirm,
  previewData,
  loading,
  error,
  totalWebsites
}) => {
  const [currentUrl, setCurrentUrl] = useState('');

  // Extract stats from preview data
  const stats = previewData?.stats || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          color: 'white',
          maxHeight: '90vh',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)', pb: 2 }}>
        <Typography variant="h6" component="div">
          Preview Results
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 6
          }}>
            <CircularProgress size={60} sx={{ color: '#faa32c' }} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              Analyzing sample websites...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This may take 30-60 seconds
            </Typography>
            {currentUrl && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                Processing: {currentUrl}
              </Typography>
            )}
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {previewData && !loading && (
          <>
            {/* Stats Cards */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 3
            }}>
              {/* Total Websites Card */}
              <Card sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #faa32c30',
                borderRadius: 3
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Websites
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#faa32c' }}>
                    {stats.totalWebsites || 0}
                  </Typography>
                </CardContent>
              </Card>

              {/* Estimated Qualified Card */}
              <Card sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #0095FF30',
                borderRadius: 3
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Est. Qualified
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h4" sx={{ color: '#0095FF' }}>
                      ~{stats.keptPercent || 0}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    (~{stats.estimatedKeptWebsites || 0} websites)
                  </Typography>
                </CardContent>
              </Card>

              {/* Estimated Disqualified Card */}
              <Card sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #FF149330',
                borderRadius: 3
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Est. Disqualified
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h4" sx={{ color: '#FF1493' }}>
                      ~{stats.excludedPercent || 0}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    (~{stats.estimatedExcludedWebsites || 0} websites)
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Info Alert */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(250, 163, 44, 0.1)',
                border: '1px solid #faa32c30',
                '& .MuiAlert-icon': { color: '#faa32c' }
              }}
            >
              This is an estimate based on {previewData.sampleSize || 10} randomly sampled websites. Actual results may vary.
            </Alert>

            {/* Sample Results Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#faa32c' }}>
                Sample Decisions
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  size="small"
                  label={`${stats.kept || 0} Qualified`}
                  sx={{
                    bgcolor: 'rgba(0, 149, 255, 0.1)',
                    color: '#0095FF',
                    fontWeight: 500,
                    border: '1px solid #0095FF30'
                  }}
                />
                <Chip
                  size="small"
                  label={`${stats.excluded || 0} Disqualified`}
                  sx={{
                    bgcolor: 'rgba(255, 20, 147, 0.1)',
                    color: '#FF1493',
                    fontWeight: 500,
                    border: '1px solid #FF149330'
                  }}
                />
                {stats.errors > 0 && (
                  <Chip
                    size="small"
                    label={`${stats.errors} Errors`}
                    sx={{
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                      color: '#ff9800',
                      fontWeight: 500,
                      border: '1px solid #ff980030'
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Results Table Header */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(200px, 1.5fr) 120px minmax(250px, 2.5fr)',
              gap: 1.5,
              p: 1.5,
              mb: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Website</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'center' }}>Decision</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Reason</Typography>
            </Box>

            {/* Results Rows */}
            <Stack spacing={0.5} sx={{ maxHeight: '300px', overflowY: 'auto' }}>
              {previewData.results && previewData.results.map((result, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(200px, 1.5fr) 120px minmax(250px, 2.5fr)',
                    gap: 1.5,
                    alignItems: 'center'
                  }}>
                    {/* Website URL/Title */}
                    <Box>
                      <Tooltip title={result.url} arrow placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer'
                          }}
                        >
                          {result.title || result.url}
                        </Typography>
                      </Tooltip>
                      <Link
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.65rem',
                          color: 'text.secondary',
                          textDecoration: 'none',
                          '&:hover': { color: '#faa32c' }
                        }}
                      >
                        {new URL(result.url).hostname}
                        <OpenInNewIcon sx={{ fontSize: '0.65rem' }} />
                      </Link>
                    </Box>

                    {/* Decision Chip */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Chip
                        label={
                          result.decision === 'KEEP' ? 'Qualified' :
                          result.decision === 'EXCLUDE' ? 'Disqualified' : 'Error'
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            result.decision === 'KEEP' ? 'rgba(0, 149, 255, 0.1)' :
                            result.decision === 'EXCLUDE' ? 'rgba(255, 20, 147, 0.1)' :
                            'rgba(255, 152, 0, 0.1)',
                          color:
                            result.decision === 'KEEP' ? '#0095FF' :
                            result.decision === 'EXCLUDE' ? '#FF1493' :
                            '#ff9800',
                          fontWeight: 500,
                          border:
                            result.decision === 'KEEP' ? '1px solid #0095FF30' :
                            result.decision === 'EXCLUDE' ? '1px solid #FF149330' :
                            '1px solid #ff980030',
                          height: '22px',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>

                    {/* Reason */}
                    <Tooltip title={result.reason || ''} arrow placement="top">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'help',
                          fontSize: '0.75rem'
                        }}
                      >
                        {result.reason}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        px: 3,
        py: 2,
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'text.secondary',
            textTransform: 'none',
            borderRadius: '4px',
            '&:hover': {
              borderColor: '#faa32c',
              color: '#faa32c',
              backgroundColor: 'rgba(250, 163, 44, 0.1)'
            }
          }}
        >
          Adjust Criteria
        </Button>

        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading || error || (stats.errors === stats.sampled)}
          sx={{
            minWidth: 180,
            textTransform: 'none',
            borderRadius: '4px',
            bgcolor: '#faa32c',
            '&:hover': { bgcolor: '#e8941f' }
          }}
        >
          {totalWebsites ? `Run Full Job (${totalWebsites} websites)` : 'Run Full Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResearchPreviewModal;
